// src/pages/finance/FinanceDashboard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { showError } from "../../utils/alert";

const arrayFrom = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  return payload.rows || payload.items || payload.data || [];
};

const sle = (value) =>
  `SLE ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}`;

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-slate-500" title={title}>
          {title}
        </p>
        <p
          className="mt-1 whitespace-nowrap text-[clamp(0.9rem,1.1vw,1.125rem)] font-bold leading-tight tracking-tight text-slate-900 tabular-nums"
          title={String(value)}
        >
          {value}
        </p>
        <p className="mt-1 truncate text-[11px] text-slate-400" title={subtitle}>
          {subtitle}
        </p>
      </div>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-xl sm:h-10 sm:w-10 ${color}`}
        aria-hidden="true"
      >
        <Icon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
      </div>
    </div>
  </div>
);

const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-slate-200 ${className}`} />
);

const FinanceDashboardSkeleton = () => (
  <div
    className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6 lg:p-8"
    role="status"
    aria-live="polite"
    aria-label="Loading finance dashboard"
  >
    <span className="sr-only">Loading finance dashboard...</span>

    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="space-y-3">
        <SkeletonBlock className="h-3 w-28" />
        <SkeletonBlock className="h-8 w-44 sm:w-56" />
        <SkeletonBlock className="h-4 w-full max-w-xl sm:w-[34rem]" />
        <SkeletonBlock className="h-4 w-4/5 max-w-md sm:hidden" />
      </div>
      <div className="flex gap-2">
        <SkeletonBlock className="h-10 w-28" />
        <SkeletonBlock className="h-10 w-36" />
      </div>
    </div>

    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row">
      <SkeletonBlock className="h-10 w-full sm:w-52" />
      <SkeletonBlock className="h-10 w-full sm:w-44" />
    </div>

    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-3">
              <SkeletonBlock className="h-3 w-24" />
              <SkeletonBlock className="h-7 w-32" />
              <SkeletonBlock className="h-3 w-28" />
            </div>
            <SkeletonBlock className="h-10 w-10 rounded-xl" />
          </div>
        </div>
      ))}
    </div>

    <div className="grid gap-4 lg:grid-cols-3">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-1">
        <SkeletonBlock className="h-4 w-28" />
        <div className="mt-4 space-y-2">
          {[0, 1, 2].map((item) => (
            <SkeletonBlock key={item} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white lg:col-span-2">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="space-y-2">
            <SkeletonBlock className="h-4 w-28" />
            <SkeletonBlock className="h-3 w-48" />
          </div>
          <SkeletonBlock className="h-3 w-14" />
        </div>
        <div className="divide-y divide-slate-100">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="flex items-center justify-between gap-3 px-5 py-4">
              <div className="space-y-2">
                <SkeletonBlock className="h-4 w-36 sm:w-44" />
                <SkeletonBlock className="h-3 w-24 sm:w-32" />
              </div>
              <div className="flex flex-col items-end gap-2">
                <SkeletonBlock className="h-4 w-20 sm:w-24" />
                <SkeletonBlock className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function FinanceDashboard() {
  const [loading, setLoading] = useState(true);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [payments, setPayments] = useState([]);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [yearId, setYearId] = useState("");
  const [termId, setTermId] = useState("");
  const [policy, setPolicy] = useState(null);

  const visibleTerms = useMemo(
    () =>
      terms.filter(
        (term) => !yearId || Number(term.academic_year_id) === Number(yearId),
      ),
    [terms, yearId],
  );

  const loadFinance = useCallback(async (selectedYearId, selectedTermId = "") => {
    const params = {};
    if (selectedYearId) params.academic_year_id = selectedYearId;
    if (selectedTermId) params.academic_term_id = selectedTermId;

    const [summaryResponse, paymentsResponse] = await Promise.all([
      apiClient.get("/finance/summary", { params }),
      apiClient.get("/finance/payments", { params: { ...params, page: 1, limit: 6 } }),
    ]);
    setSummary(summaryResponse?.data?.data || {});
    setPayments(arrayFrom(paymentsResponse));
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const [yearResponse, termResponse, policyResponse] = await Promise.all([
          apiClient.get("/academic-years"),
          apiClient.get("/academic-terms"),
          apiClient.get("/finance/policy"),
        ]);

        const yearList = arrayFrom(yearResponse);
        const termList = arrayFrom(termResponse);
        const currentYear = yearList.find((item) => item.is_current) || yearList[0];
        const initialYearId = currentYear ? String(currentYear.id) : "";

        setYears(yearList);
        setTerms(termList);
        setPolicy(policyResponse?.data?.data || null);
        setYearId(initialYearId);

        // Always open the dashboard on "All Terms".
        setTermId("");

        if (initialYearId) {
          // Wait for finance data too, so the skeleton remains until the
          // complete dashboard is ready to display.
          await loadFinance(initialYearId, "");
        } else {
          setSummary({});
          setPayments([]);
        }
      } catch (error) {
        console.error("Finance dashboard load error:", error);
        showError({ text: "Failed to load finance dashboard information." });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [loadFinance]);

  const refreshFinance = async (nextYearId, nextTermId) => {
    try {
      setFinanceLoading(true);
      if (nextYearId) {
        await loadFinance(nextYearId, nextTermId);
      } else {
        setSummary({});
        setPayments([]);
      }
    } catch (error) {
      console.error("Finance dashboard error:", error);
      showError({ text: "Failed to load finance information." });
    } finally {
      setFinanceLoading(false);
    }
  };

  const handleYearChange = async (event) => {
    const nextYear = event.target.value;
    setYearId(nextYear);
    // A year change also returns the term filter to "All Terms".
    setTermId("");
    await refreshFinance(nextYear, "");
  };

  const handleTermChange = async (event) => {
    const nextTerm = event.target.value;
    setTermId(nextTerm);
    await refreshFinance(yearId, nextTerm);
  };

  if (loading || financeLoading) return <FinanceDashboardSkeleton />;

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">School Finance</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl">{policy?.is_free_education ? "Free Education" : "School Fees"}</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            {policy?.is_free_education ? "Track the school's Free Quality School Education status. Students are not charged tuition." : "Record verified bank payments and monitor school-fee balances. PawaSchool does not process the payment itself."}
          </p>
        </div>
        {!policy?.is_free_education && <div className="flex flex-wrap gap-2">
          <Link to="/app/finance/fees" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Set Class Fees
          </Link>
          <Link to="/app/finance/payments/new" className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
            <PlusCircleIcon className="h-4 w-4" /> Record Payment
          </Link>
        </div>}
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row">
        <select value={yearId} onChange={handleYearChange} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400">
          <option value="">Academic year</option>
          {years.map((year) => <option key={year.id} value={year.id}>{year.name || year.year || year.academic_year}</option>)}
        </select>
        <select value={termId} onChange={handleTermChange} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400">
          <option value="">All Terms</option>
          {visibleTerms.map((term) => <option key={term.id} value={term.id}>{term.name || term.short_name || `Term ${term.term_number}`}</option>)}
        </select>
      </div>

      {policy?.is_free_education && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><ShieldCheckIcon className="h-7 w-7" /></div>
            <div><h2 className="font-bold text-emerald-900">Free Quality School Education enabled</h2><p className="mt-1 text-sm leading-6 text-emerald-800">This {String(policy.school_type || "government").replaceAll("_", " ")} school does not charge student tuition. Class fees and new tuition payments are disabled, while historical records remain available.</p></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Expected Fees" value={sle(summary.expected)} subtitle="Configured class fees" icon={DocumentTextIcon} color="bg-blue-50 text-blue-600" />
        <StatCard title="Collected" value={sle(summary.collected)} subtitle="Verified bank payments" icon={CheckCircleIcon} color="bg-emerald-50 text-emerald-600" />
        <StatCard title="Outstanding" value={sle(summary.outstanding)} subtitle="Remaining student balances" icon={ClockIcon} color="bg-amber-50 text-amber-600" />
        <StatCard title={policy?.is_free_education ? "Students Covered" : "Collection Rate"} value={policy?.is_free_education ? String(summary.students_covered || 0) : `${Number(summary.collection_rate || 0).toFixed(1)}%`} subtitle={policy?.is_free_education ? "Active students with free tuition" : `${summary.students_charged || 0} student fee records`} icon={policy?.is_free_education ? UserGroupIcon : CurrencyDollarIcon} color="bg-violet-50 text-violet-600" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-1">
          <h2 className="text-sm font-bold text-slate-900">Quick Actions</h2>
          <div className="mt-4 space-y-2">
            {(policy?.is_free_education ? [
              ["School Funding Setup", "/app/finance/fees", ShieldCheckIcon],
              ["Historical Payments", "/app/finance/payments", DocumentTextIcon],
            ] : [
              ["Record Bank Payment", "/app/finance/payments/new", BanknotesIcon],
              ["Set Class Fees", "/app/finance/fees", CurrencyDollarIcon],
              ["Payment History", "/app/finance/payments", DocumentTextIcon],
            ]).map(([label, href, Icon]) => (
              <Link key={href} to={href} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 text-sm font-medium text-slate-700 hover:border-primary-200 hover:bg-primary-50">
                <Icon className="h-5 w-5 text-primary-600" /> {label}
              </Link>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white lg:col-span-2">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Recent Payments</h2>
              <p className="text-xs text-slate-400">Latest verified bank receipts recorded</p>
            </div>
            <Link to="/app/finance/payments" className="text-xs font-semibold text-primary-600 hover:text-primary-700">View all</Link>
          </div>
          {payments.length ? (
            <div className="divide-y divide-slate-100">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <Link to={`/app/finance/students/${payment.student_id}`} className="truncate text-sm font-semibold text-slate-800 hover:text-primary-600">
                      {payment.student ? `${payment.student.first_name || ""} ${payment.student.last_name || ""}`.trim() : `Student #${payment.student_id}`}
                    </Link>
                    <p className="truncate text-xs text-slate-400">Bank ref: {payment.bank_reference}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-600">{sle(payment.amount_paid)}</p>
                    <p className="text-[11px] text-slate-400">{payment.bank_payment_date}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-12 text-center">
              <UserGroupIcon className="mx-auto h-9 w-9 text-slate-300" />
              <p className="mt-2 text-sm text-slate-500">No payments recorded for this selection.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}